/**
 * The workspaces of the Organisation this window administers
 * (docs/adr/39-two-admin-clients-the-elaso-instance-admin-client-and-the-organisation-admin-client.md).
 *
 * A thin wrapper. The panel (`components/workspaces/workspace-admin-panel.tsx`)
 * is presentational, and this file owns only which procedures answer it, and
 * which Organisation they are aimed at.
 *
 * Every call below names the Organisation the window opened on. That id is a
 * REQUEST, not a permission: the server re-decides it on every procedure, so a
 * page that named an Organisation the caller may not administer would be
 * refused rather than obeyed (`lib/authz/gate.ts`).
 */

import type { OrganizationWorkspaceRow } from "@elaso/shared/types/organization";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { ulid } from "ulid";
import { AdminReadFailed } from "@/components/admin/admin-status";
import { useAdministeredOrganization } from "@/components/organization/administered-organization";
import {
	ArrangedAccessCells,
	useArrangedAccess,
} from "@/components/organization/arranged-access";
import { useCourseImport } from "@/components/organization/external-workspaces/course-import";
import {
	CoursesWithNoWorkspace,
	courseNamesByWorkspace,
	SharedWorkspacesPartnerIn,
	useCourseList,
} from "@/components/organization/external-workspaces/course-lists";
import {
	ProgramDirectorCells,
	useProgramDirectors,
} from "@/components/organization/program-directors";
import {
	type CloseThen,
	WorkspaceAdminPanel,
} from "@/components/workspaces/workspace-admin-panel";
import { useWorkspaceList } from "@/hooks/use-workspace-list";
import { ws } from "@/lib/websocket-wrapper";

export const Route = createFileRoute("/organization/$organizationId/")({
	component: OrganizationWorkspacesPage,
});

function OrganizationWorkspacesPage() {
	const organization = useAdministeredOrganization();
	const organizationId = organization.organizationId;
	const { list, load } = useWorkspaceList("organization.listWorkspaces", {
		organizationId,
	});

	// Re-read when the administered Organisation changes, as well as on mount.
	// The hook syncs its request into a ref in its own effect, which runs before
	// this one, so `load` here already asks about the Organisation just chosen
	// rather than the one being left. `load` is stable by construction, so
	// listing it costs nothing and keeps the dependency list honest.
	useEffect(() => {
		load();
	}, [organizationId, load]);

	const programDirectors = useProgramDirectors(organization);
	const reloadProgramDirectors = programDirectors.reload;
	// Arranging or ending access gives or takes the Program Director role, so the
	// directors are read again with the workspaces.
	const afterArrangedAccess = useCallback(() => {
		load();
		reloadProgramDirectors();
	}, [load, reloadProgramDirectors]);
	const arrangedAccess = useArrangedAccess(organization, afterArrangedAccess);
	// The courses feed the Shared Workspaces section below the table, and are
	// read alongside it rather than before it, so the table never waits on the
	// Organisation's own system.
	const courses = useCourseList(organization);
	const reloadCourses = courses.reload;
	// A course run can make a workspace and link it, so both lists are read again.
	const afterCourseRun = useCallback(() => {
		load();
		reloadCourses();
	}, [load, reloadCourses]);
	const courseImport = useCourseImport(organization, afterCourseRun);
	// The Course column names each linked workspace's course from the same read.
	const courseNames = useMemo(
		() => courseNamesByWorkspace(organization, courses.read),
		[organization, courses.read]
	);
	const courseNameOf = useCallback(
		(workspace: OrganizationWorkspaceRow) =>
			courseNames.get(workspace.id) ?? null,
		[courseNames]
	);
	const columns = useMemo(
		() => [...programDirectors.columns, ...arrangedAccess.columns],
		[programDirectors.columns, arrangedAccess.columns]
	);
	const rowActions = useCallback(
		(workspace: OrganizationWorkspaceRow) => [
			...programDirectors.rowActions(workspace),
			...(arrangedAccess.rowActions?.(workspace) ?? []),
			...courseImport.rowActions(workspace),
		],
		[
			programDirectors.rowActions,
			arrangedAccess.rowActions,
			courseImport.rowActions,
		]
	);
	const createFromCourse =
		organization.adapterKind === null
			? undefined
			: (closeThen: CloseThen) => (
					<CoursesWithNoWorkspace
						closeThen={closeThen}
						flows={courseImport}
						organization={organization}
					/>
				);

	async function handleCreate(name: string) {
		try {
			await ws.sendWithPromise({
				id: ulid(),
				procedure: "organization.createWorkspace",
				data: { organizationId, name },
			});
			toast.success(`Created workspace ${name}`);
			load();
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(`Create failed: ${message}`);
			// Rethrown so the panel leaves the typed name in place. A refused name is
			// usually one edit away from a good one.
			throw err;
		}
	}

	async function handleRename(workspaceId: string, name: string) {
		try {
			await ws.sendWithPromise({
				id: ulid(),
				procedure: "organization.renameWorkspace",
				data: { organizationId, workspaceId, name },
			});
			load();
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(`Rename failed: ${message}`);
			throw err;
		}
	}

	async function handleSetEnabled(workspaceId: string, enabled: boolean) {
		try {
			await ws.sendWithPromise({
				id: ulid(),
				procedure: "organization.setWorkspaceEnabled",
				data: { organizationId, workspaceId, enabled },
			});
			load();
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(`Update failed: ${message}`);
			throw err;
		}
	}

	async function handleSetExternalRef(
		workspaceId: string,
		externalRef: string | null
	) {
		try {
			await ws.sendWithPromise({
				id: ulid(),
				procedure: "organization.setWorkspaceExternalRef",
				data: { organizationId, workspaceId, externalRef },
			});
			load();
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(`Update failed: ${message}`);
			throw err;
		}
	}

	return (
		<div className="flex flex-col items-center gap-4 p-4 md:p-8">
			{programDirectors.failure ? (
				<AdminReadFailed
					message="Could not load the Program Directors."
					onRetry={programDirectors.failure.retry}
				/>
			) : null}
			{arrangedAccess.failure ? (
				<AdminReadFailed
					message="Could not load the arranged access."
					onRetry={arrangedAccess.failure.retry}
				/>
			) : null}
			<ProgramDirectorCells value={programDirectors.cells}>
				<ArrangedAccessCells value={arrangedAccess.cells}>
					<WorkspaceAdminPanel
						columns={columns}
						courseNameOf={courseNameOf}
						createFromCourse={createFromCourse}
						list={list}
						onCreate={handleCreate}
						onRename={handleRename}
						onRetry={load}
						onSetEnabled={handleSetEnabled}
						onSetExternalRef={handleSetExternalRef}
						rowActions={rowActions}
					/>
				</ArrangedAccessCells>
			</ProgramDirectorCells>
			<SharedWorkspacesPartnerIn
				flows={courseImport}
				organization={organization}
				read={courses.read}
				retry={reloadCourses}
				workspaces={list}
			/>
			{programDirectors.dialog}
			{arrangedAccess.dialog}
			{courseImport.dialog}
		</div>
	);
}
